import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient, getErrorMessage } from '../client';
import { queryKeys } from '../queryKeys';
import { calculateTotalPages } from '@/lib/pagination';
import type {
  Warehouse,
  CreateWarehouseDto,
  UpdateWarehouseDto,
  PaginatedResponse,
  PaginationParams,
} from '../types/common';

// API response format for warehouses
interface WarehouseApiResponse {
  warehouses: Warehouse[];
  total: number;
}

/**
 * Default list — active-only. Used by the warehouse switcher and any form
 * that needs to show pickable warehouses. Do NOT use this on the admin
 * management page — use `useAllWarehouses()` instead.
 */
export function useWarehouses(params?: PaginationParams) {
  return useQuery({
    queryKey: queryKeys.warehouses.list(params),
    queryFn: async () => {
      const { data } = await apiClient.get<WarehouseApiResponse>(
        '/api/warehouses',
        {
          params,
        },
      );
      // Map API response to frontend expected format
      return {
        data: data.warehouses,
        total: data.total,
        page: params?.page ?? 1,
        limit: params?.limit ?? 20,
        totalPages: calculateTotalPages(data.total, params?.limit ?? 20),
      } as PaginatedResponse<Warehouse>;
    },
  });
}

/**
 * Admin-only list — includes BOTH active and inactive warehouses. Used by
 * the Warehouses management page so admins can see and re-activate
 * deactivated warehouses. Uses a distinct query key so cache doesn't
 * collide with `useWarehouses()`.
 */
export function useAllWarehouses(params?: PaginationParams) {
  return useQuery({
    queryKey: queryKeys.warehouses.listAll(params),
    queryFn: async () => {
      const { data } = await apiClient.get<WarehouseApiResponse>(
        '/api/warehouses',
        {
          params: { ...params, includeInactive: 'true' },
        },
      );
      return {
        data: data.warehouses,
        total: data.total,
        page: params?.page ?? 1,
        limit: params?.limit ?? 20,
        totalPages: calculateTotalPages(data.total, params?.limit ?? 20),
      } as PaginatedResponse<Warehouse>;
    },
  });
}

// Get single warehouse
export function useWarehouse(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.warehouses.detail(id),
    queryFn: async () => {
      const { data } = await apiClient.get<Warehouse>(`/api/warehouses/${id}`);
      return data;
    },
    enabled: !!id,
  });
}

/**
 * Shared invalidator — every warehouse mutation must refresh all three
 * caches so the switcher, management page, and user's accessible list
 * stay in sync.
 */
function invalidateAllWarehouseCaches(
  queryClient: ReturnType<typeof useQueryClient>,
) {
  queryClient.invalidateQueries({ queryKey: queryKeys.warehouses.all });
  queryClient.invalidateQueries({ queryKey: queryKeys.warehouses.allList });
  queryClient.invalidateQueries({ queryKey: queryKeys.warehouses.accessible });
}

// Create warehouse
export function useCreateWarehouse() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (dto: CreateWarehouseDto) => {
      const { data } = await apiClient.post<Warehouse>('/api/warehouses', dto);
      return data;
    },
    onSuccess: () => {
      invalidateAllWarehouseCaches(queryClient);
    },
    onError: (error) => {
      console.error('Failed to create warehouse:', getErrorMessage(error));
    },
  });
}

// Update warehouse
export function useUpdateWarehouse() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      dto,
    }: {
      id: string;
      dto: UpdateWarehouseDto;
    }) => {
      const { data } = await apiClient.patch<Warehouse>(
        `/api/warehouses/${id}`,
        dto,
      );
      return data;
    },
    onSuccess: (data) => {
      invalidateAllWarehouseCaches(queryClient);
      queryClient.setQueryData(queryKeys.warehouses.detail(data.id), data);
    },
    onError: (error) => {
      console.error('Failed to update warehouse:', getErrorMessage(error));
    },
  });
}

// Delete warehouse
export function useDeleteWarehouse() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/api/warehouses/${id}`);
      return id;
    },
    onSuccess: () => {
      invalidateAllWarehouseCaches(queryClient);
    },
    onError: (error) => {
      console.error('Failed to delete warehouse:', getErrorMessage(error));
    },
  });
}

// Activate warehouse
export function useActivateWarehouse() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.patch<Warehouse>(
        `/api/warehouses/${id}/activate`,
      );
      return data;
    },
    onSuccess: (data) => {
      invalidateAllWarehouseCaches(queryClient);
      queryClient.setQueryData(queryKeys.warehouses.detail(data.id), data);
    },
    onError: (error) => {
      console.error('Failed to activate warehouse:', getErrorMessage(error));
    },
  });
}

// Deactivate warehouse
export function useDeactivateWarehouse() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.patch<Warehouse>(
        `/api/warehouses/${id}/deactivate`,
      );
      return data;
    },
    onSuccess: (data) => {
      invalidateAllWarehouseCaches(queryClient);
      queryClient.setQueryData(queryKeys.warehouses.detail(data.id), data);
    },
    onError: (error) => {
      console.error('Failed to deactivate warehouse:', getErrorMessage(error));
    },
  });
}
